import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalActionBar from 'components/modal-action-bar';
import SelectField from 'components/select-field';
import { ListingEntry } from 'graphql/listings/queries';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(0, 2, 2),
  },
  title: {
    padding: theme.spacing(2, 2, 1),
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  field: {
    marginBottom: theme.spacing(2),
  },
}));

type PartyModalChangeKindsProps = {
  listingEntries: ListingEntry[];
  handleCloseModal: () => void;
  open: boolean;
  enableChangeKinds: ListingEntry[];
  handleChangeKind: (data: { kindToChange: string; newKind: string }) => void;
  isSubmitting?: boolean;
};

const PartyModalChangeKinds = ({
  listingEntries,
  handleCloseModal,
  open,
  enableChangeKinds,
  handleChangeKind,
  isSubmitting = false,
}: PartyModalChangeKindsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { control, handleSubmit, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      kindToChange: '',
      newKind: '',
    },
  });

  const onSubmit = (data: { kindToChange: string; newKind: string }) => {
    handleChangeKind(data);
  };

  const handleDialogClose = () => {
    if (isSubmitting) return;
    handleCloseModal();
  };

  return (
    <Dialog
      onClose={handleDialogClose}
      open={open}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle className={classes.title}>{t('orders:selectNewKindTitle')}</DialogTitle>
      <DialogContent className={classes.container}>
        <Box className={classes.field}>
          <Controller
            control={control}
            name="kindToChange"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectField
                {...field}
                emptyValue
                value={enableChangeKinds.length > 0 ? field.value : ''}
                inputRef={field.ref}
                data={[...enableChangeKinds].sort((entryA, entryB) =>
                  entryA.description.localeCompare(entryB.description)
                )}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('orders:kindToChangeLabel')}
                handleChange={field.onChange}
              />
            )}
          />
        </Box>
        <Box className={classes.field}>
          <Controller
            control={control}
            name="newKind"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectField
                {...field}
                emptyValue
                value={listingEntries.length > 0 ? field.value : ''}
                inputRef={field.ref}
                data={[...listingEntries].sort((entryA, entryB) =>
                  entryA.description.localeCompare(entryB.description)
                )}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('orders:newKindLabel')}
                handleChange={field.onChange}
              />
            )}
          />
        </Box>
      </DialogContent>
      <ModalActionBar
        onCancel={handleDialogClose}
        submitText={t('common:save')}
        submitType="button"
        onSubmit={handleSubmit(onSubmit)}
        isSubmitting={isSubmitting}
        submitDisabled={!formState.isValid}
      />
    </Dialog>
  );
};

export default PartyModalChangeKinds;
